import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ChartContextPayload, ChartLegendMetadata } from 'src/utils/chartContext/types';
import { prepareChartContextPayload } from 'src/utils/chartContext/preparePayload';

/**
 * Entry for a single registered chart in the store.
 */
interface ChartEntry {
  options: Record<string, unknown>;
  group?: string;
  legendMetadata?: ChartLegendMetadata;
}

/**
 * Chart Context Store State
 *
 * This store manages chart options for the "Ask Advisor" feature.
 * Chart components register their Highcharts options tagged with an
 * optional context group, and useAskAdvisorAction reads scoped
 * payloads from this store.
 *
 * Behavior:
 * - In-memory only (no localStorage/sessionStorage)
 * - Persists during SPA navigation
 * - Clears on hard page reload
 * - Self-cleans stale entries when URL pathname changes
 * - Supports section-level scoping via context groups
 *
 * @see src/utils/chartContext/ARCHITECTURE.md
 */
export interface ChartContextState {
  // Registered charts by ID, each with options and optional group tag
  charts: Record<string, ChartEntry>;

  // Tracks last URL pathname for self-cleaning on navigation
  lastPathname: string;

  // Actions
  registerChart: (chartId: string, options: Record<string, unknown>, group?: string, legendMetadata?: ChartLegendMetadata) => void;
  unregisterChart: (chartId: string) => void;
  clearAll: () => void;

  // Selectors
  getRegisteredChartIds: () => string[];
  getChartCount: () => number;
  getPayload: (group?: string) => ChartContextPayload | null;
}

/**
 * Zustand store for managing chart context for the Advisor feature.
 *
 * Usage:
 * ```tsx
 * // In chart component (via useRegisterChartContext hook)
 * // Registration is automatic -- group comes from React Context
 *
 * // In useAskAdvisorAction -- read scoped payload
 * const getPayload = useChartContextStore((state) => state.getPayload);
 * const payload = getPayload('traffic'); // only charts in "traffic" group
 * ```
 */
export const useChartContextStore = create<ChartContextState>()(
  devtools(
    (set, get) => ({
      // Initial state
      charts: {},
      lastPathname: typeof window !== 'undefined' ? window.location.pathname : '',

      // Actions
      registerChart: (chartId: string, options: Record<string, unknown>, group?: string, legendMetadata?: ChartLegendMetadata) => {
        const currentPathname = typeof window !== 'undefined' ? window.location.pathname : '';

        set(
          (state) => {
            // Self-clean: if URL pathname changed, clear all stale charts from previous entity/page
            if (state.lastPathname && state.lastPathname !== currentPathname) {
              return {
                charts: { [chartId]: { options, group, legendMetadata } },
                lastPathname: currentPathname
              };
            }

            return {
              charts: { ...state.charts, [chartId]: { options, group, legendMetadata } },
              lastPathname: currentPathname
            };
          },
          false,
          'registerChart'
        );
      },

      unregisterChart: (chartId: string) => {
        set(
          (state) => {
            const { [chartId]: _, ...rest } = state.charts;
            return { charts: rest };
          },
          false,
          'unregisterChart'
        );
      },

      clearAll: () => {
        set({ charts: {} }, false, 'clearAll');
      },

      // Selectors
      getRegisteredChartIds: () => Object.keys(get().charts),

      getChartCount: () => Object.keys(get().charts).length,

      getPayload: (group?: string) => {
        const { charts } = get();
        let chartEntries = Object.entries(charts);

        // Filter by group when provided
        if (group) {
          chartEntries = chartEntries.filter(([, entry]) => entry.group === group);
        }

        if (chartEntries.length === 0) {
          return null;
        }

        const { payload } = prepareChartContextPayload(
          chartEntries.map(([chartId, entry]) => ({
            chartId,
            options: entry.options,
            legendMetadata: entry.legendMetadata
          }))
        );

        return payload;
      }
    }),
    { name: 'chart-context-store' }
  )
);
